import { JSX, useContext, useEffect, useMemo } from "react";
import { BulkTargetingWizardContext } from "./TargetingWizardContext";
import { BulkTargetingWizardStepProps } from "./interfaces";
import ReportColumnConfiguration from "@app/targeting/import/components/ReportColumnConfiguration";
import PageSection from "@app/shared/PageSection";
import { getTargetingReportSections, TargetingReportSection } from "@app/targeting/import/reportSections";

const isConfigurable = (section: TargetingReportSection): section is TargetingReportSection & { columnsEndpoint: NonNullable<TargetingReportSection["columnsEndpoint"]> } =>
  !!section.columnsEndpoint;

const TargetingWizardColumnConfiguration = (props: BulkTargetingWizardStepProps): JSX.Element => {
  const wizardContext = useContext(BulkTargetingWizardContext);

  const reportSections = useMemo(
    () => getTargetingReportSections(wizardContext.isDistinctServicingProviderReport, wizardContext.projectType),
    [wizardContext.isDistinctServicingProviderReport, wizardContext.projectType]
  );

  // Only sections selected on the previous step and that require column configuration (Target Upload File doesn't).
  const columnConfigurationReports = useMemo(() => {
    if (!wizardContext.selectedReports || wizardContext.selectedReports.length === 0) {
      return [];
    }

    const selectedKeys = new Set(wizardContext.selectedReports);
    return reportSections.filter(section => selectedKeys.has(section.key) && isConfigurable(section));
  }, [wizardContext.selectedReports, reportSections]);

  useEffect(() => {
    const currentConfigs = wizardContext.columnConfigurations || {};
    let needsUpdate = false;
    const updatedConfigs = { ...currentConfigs };

    columnConfigurationReports.forEach(report => {
      if (!currentConfigs[report.key]) {
        updatedConfigs[report.key] = {
          columnsEndpoint: report.columnsEndpoint,
          key: report.key,
          reportType: report.reportType,
          title: report.columnConfigurationTitle,
        };
        needsUpdate = true;
      }
    });

    Object.keys(currentConfigs).forEach(sectionKey => {
      if (!columnConfigurationReports.some(report => report.key === sectionKey)) {
        delete updatedConfigs[sectionKey];
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      wizardContext.setColumnConfigurations(updatedConfigs);
    }
  }, [columnConfigurationReports, wizardContext.columnConfigurations]);

  // Validate step - all selected reports should have column configuration except Target Upload File which doesn't require configuration
  useEffect(() => {
    const hasSelectedReports = (wizardContext.selectedReports?.length ?? 0) > 0;
    const allConfigurableReportsConfigured = columnConfigurationReports.every(report => {
      const config = wizardContext.columnConfigurations?.[report.key];
      return config && config.reportType && config.columnsEndpoint;
    });

    props.stepIsValid(hasSelectedReports && allConfigurableReportsConfigured);
  }, [columnConfigurationReports, wizardContext.columnConfigurations, wizardContext.selectedReports, props.stepIsValid]);

  return (
    <PageSection>
      {columnConfigurationReports.length === 0 ? (
        <h3 className="text-muted no-content-text" data-testid="targeting-wizard-column-configuration__no-reports-message">No configurable reports selected.</h3>
      ) : (
        <div className="container">
          {columnConfigurationReports.map((report) => (
            <div key={report.key} className="mb-4">
              <ReportColumnConfiguration
                reportType={report.reportType}
                sectionKey={report.key}
                title={report.columnConfigurationTitle}
                columnsEndpoint={report.columnsEndpoint}
              />
            </div>
          ))}
        </div>
      )}
    </PageSection>
  );
};

export default TargetingWizardColumnConfiguration;
